import { apiService } from './api';
import {
  MediaAsset,
  MediaUploadOptions,
  MediaGalleryFilter,
  StorageHealthReport,
  StorageRepairResult,
} from '../types/media';
import { createHttpsUrl } from '../utils/https';

export class MediaService {
  async uploadFile(file: File, options: MediaUploadOptions = {}): Promise<MediaAsset> {
    const formData = new FormData();
    formData.append('file', file);
    
    const params = new URLSearchParams();
    if (options.stakeholderId) {
      params.append('stakeholderId', options.stakeholderId);
    }
    if (options.organizationId) {
      params.append('organizationId', options.organizationId);
    }

    const url = `/api/media/upload${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await apiService.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  async getMediaAssets(filter: MediaGalleryFilter = {}): Promise<MediaAsset[]> {
    const params = new URLSearchParams();

    if (filter.stakeholderId) params.append('stakeholderId', filter.stakeholderId);
    if (filter.organizationId) params.append('organizationId', filter.organizationId);
    if (filter.type) params.append('type', filter.type);
    if (filter.search) params.append('search', filter.search);

    const url = `/api/media${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiService.get(url);

    // Backend now returns { data, total } but maintain backwards compat
    const assets = response.data?.data || response.data;
    return Array.isArray(assets) ? assets : [];
  }

  async getMediaAssetsPaginated(filter: MediaGalleryFilter & {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    unassigned?: boolean;
  } = {}): Promise<{ data: MediaAsset[]; total: number }> {
    const params = new URLSearchParams();

    if (filter.stakeholderId) params.append('stakeholderId', filter.stakeholderId);
    if (filter.organizationId) params.append('organizationId', filter.organizationId);
    if (filter.type) params.append('type', filter.type);
    if (filter.search) params.append('search', filter.search);
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortOrder) params.append('sortOrder', filter.sortOrder);
    if (filter.unassigned) params.append('unassigned', 'true');

    const url = `/api/media${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiService.get(url);
    const payload = response.data;
    return { data: payload.data || [], total: payload.total ?? 0 };
  }

  async getCounts(): Promise<{ total: number; counts: Record<string, number>; unassigned: number }> {
    const response = await apiService.get('/api/media/counts');
    return response.data;
  }

  async linkBusinessCards(): Promise<{ linked: number }> {
    const response = await apiService.post('/api/media/link-business-cards');
    return response.data;
  }

  /** Admin: checks both stores and which media files are missing */
  async getStorageHealth(): Promise<StorageHealthReport> {
    const response = await apiService.get('/api/media/storage/health');
    return response.data;
  }

  /** Admin: copies files found only in the fallback store into the primary store */
  async repairStorage(): Promise<StorageRepairResult> {
    const response = await apiService.post('/api/media/storage/repair');
    return response.data;
  }

  async getMediaAsset(id: string): Promise<MediaAsset> {
    const response = await apiService.get(`/api/media/${id}`);
    return response.data;
  }

  async deleteMediaAsset(id: string): Promise<void> {
    await apiService.delete(`/api/media/${id}`);
  }

  getDownloadUrl(id: string): string {
    return createHttpsUrl(`/api/media/${id}/download`);
  }

  getViewUrl(id: string): string {
    return createHttpsUrl(`/api/media/${id}/view`);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📈';
    return '📎';
  }

  isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  isVideoFile(mimeType: string): boolean {
    return mimeType.startsWith('video/');
  }

  isAudioFile(mimeType: string): boolean {
    return mimeType.startsWith('audio/');
  }
}

export const mediaService = new MediaService();
